import { Item } from "@/lib/api/products";
import { collection, query, where, getDocs, limit, orderBy } from "firebase/firestore";
import { getFirebaseDb as getDb } from "@/lib/api/firebase-ultra-safe";
import { ruleBasedRecommendation, RecommendationScore } from "./recommendation/ruleBased";
import { aiBasedRecommendation } from "./recommendation/aiFuture";

/**
 * Firebase에서 유사한 상품 추천 가져오기
 * 
 * @param currentItem 현재 상품
 * @param maxResults 최대 추천 개수 (기본 6개)
 * @param useAI AI 기반 추천 사용 여부 (기본 false, 룰베이스 사용)
 * @returns 추천된 상품 배열
 */
export async function getRecommendedItems(
  currentItem: Item,
  maxResults: number = 6,
  useAI: boolean = false
): Promise<Item[]> {
  try {
    const db = getDb();
    if (!db) {
      console.error("Firebase DB 초기화 실패");
      return [];
    }

    const currentCategory = (currentItem as any).category || "";
    const itemsRef = collection(db, "items");

    // 1. 같은 카테고리 + 활성 상태인 상품만 필터링
    // 인덱스 없이 작동하도록 클라이언트 사이드에서 정렬
    let q;
    if (currentCategory) {
      // category와 status만 필터링 (인덱스 필요 없음)
      q = query(
        itemsRef,
        where("category", "==", currentCategory),
        where("status", "==", "active"),
        limit(100) // 더 많이 가져와서 클라이언트에서 정렬
      );
    } else {
      // 카테고리가 없으면 활성 상품만 가져오기
      q = query(
        itemsRef,
        where("status", "==", "active"),
        limit(100)
      );
    }

    const snapshot = await getDocs(q);
    const candidateItems: Item[] = [];

    snapshot.forEach((doc) => {
      const data = doc.data();
      // 현재 상품 제외
      if (doc.id !== currentItem.id) {
        candidateItems.push({
          id: doc.id,
          ...data,
        } as Item);
      }
    });

    // 클라이언트 사이드에서 createdAt 기준으로 정렬
    candidateItems.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.()?.getTime() || 
        (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0) ||
        (typeof a.createdAt === 'string' ? new Date(a.createdAt).getTime() : 0);
      const bTime = b.createdAt?.toDate?.()?.getTime() || 
        (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0) ||
        (typeof b.createdAt === 'string' ? new Date(b.createdAt).getTime() : 0);
      return bTime - aTime; // 최신순
    });

    // 상위 50개만 사용
    const topCandidates = candidateItems.slice(0, 50);

    if (topCandidates.length === 0) {
      return [];
    }

    // 2. 추천 알고리즘 적용
    let scoredItems: RecommendationScore[];
    if (useAI) {
      scoredItems = await aiBasedRecommendation(currentItem, topCandidates);
    } else {
      scoredItems = ruleBasedRecommendation(currentItem, topCandidates);
    }

    // 3. 점수 높은 순으로 정렬
    scoredItems.sort((a, b) => b.score - a.score);

    // 4. 상위 N개 반환
    const recommended = scoredItems
      .slice(0, maxResults)
      .map((scored) => scored.item);

    // 디버깅용 로그
    if (process.env.NODE_ENV === "development") {
      console.log("추천 결과:", {
        currentItem: currentItem.id,
        candidates: topCandidates.length,
        recommended: recommended.length,
        topScores: scoredItems.slice(0, 3).map((s) => ({
          id: s.item.id,
          score: s.score,
          reasons: s.reasons,
        })),
      });
    }

    return recommended;
  } catch (error) {
    console.error("추천 상품 가져오기 실패:", error);
    return [];
  }
}

/**
 * 클라이언트 사이드에서 사용할 수 있는 간단한 추천 함수
 * (이미 로드된 items 배열에서 추천)
 */
export function getRecommendedItemsFromArray(
  currentItem: Item,
  allItems: Item[],
  maxResults: number = 6
): Item[] {
  // 현재 상품 제외
  const candidateItems = allItems.filter((item) => item.id !== currentItem.id);

  if (candidateItems.length === 0) {
    return [];
  }

  // 룰베이스 추천 적용
  const scoredItems = ruleBasedRecommendation(currentItem, candidateItems);

  // 점수 높은 순으로 정렬
  scoredItems.sort((a, b) => b.score - a.score);

  // 상위 N개 반환
  return scoredItems.slice(0, maxResults).map((scored) => scored.item);
}

